import { Card, CardBody, Row, Col, Badge, Button } from "reactstrap"
import "./VehicleShortInfoCard.css"
import commingSoonImg from '../../../../assets/images/photo-coming-soon.jpg'

const VehicleShortInfoCard = ({ vehicle }) => {
  if (!vehicle) return null

  const {
    stock_id,
    brand,
    model,
    package: pkg,
    year,
    mileage,
    exterior_color,
    interior_color,
    engine_type,
    fuel_type,
    key_type,
    transmission,
    auction_grade,
    seats,
    doors,
    location,
    primary_image,
    short_descriptions,
    status,
    engine_c_c,
    features,
    auction_sheet,
    full_chassis,
    full_engine,
    tire_size,
    chassis_code,
    showroom,
    remarks,
    brta_reg_no,
  } = vehicle

  const imageUrl =
    primary_image ||
    vehicle.images?.find(img => img.isPrimary)?.url ||
    vehicle.images?.[0]?.url ||
    commingSoonImg

  const formatNumber = num =>
    num ? num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "-"

  return (
    <Card className="vehicle-card-compact shadow-sm border-0 rounded-3 overflow-hidden mb-3">
      <div className="vehicle-card-content d-flex">
        {/* Image Section */}
        <div className="vehicle-thumb-compact position-relative">
          <img
            src={imageUrl}
            alt={`${brand?.name || ""} ${model?.name || ""}`}
            className="w-100 h-100 object-fit-cover"
          />
          <div className="position-absolute top-0 start-0 end-0 p-2 d-flex justify-content-between">
            {location?.name && (
              <Badge color="warning" className="fw-normal badge-sm">
                <i className="bx bx-map me-1"></i>
                {location.name}
              </Badge>
            )}
            {status?.name && (
              <Badge color="success" className="fw-normal badge-sm">
                <i className="bx bx-check-circle me-1"></i>
                {status.name}
              </Badge>
            )}
          </div>
          {auction_sheet && (
            <Badge
              color="info"
              className="position-absolute bottom-0 start-0 m-2 badge-sm"
            >
              <i className="bx bx-file me-1"></i>
              Auction Sheet
            </Badge>
          )}
        </div>

        {/* Content Section */}
        <CardBody className="p-3 flex-grow-1 d-flex flex-column">
          {stock_id && (
            <div className="fw-bold fs-4">
              <span className="me-2">
                <i className="bx bx-id-card me-2 fs-4"></i>
                {stock_id}
              </span>
            </div>
          )}

          {/* Header */}
          <div className="mb-2">
            <div className="d-flex justify-content-between align-items-start mb-1">
              <div>
                <h6 className="mb-0 text-dark fw-bold">
                  {brand?.name} {model?.name}
                  {engine_type?.name && (
                    <span className="text-muted"> • {engine_type.name}</span>
                  )}
                  {pkg?.name && (
                    <span className="text-muted"> • {pkg.name}</span>
                  )}
                </h6>
                <div className="d-flex align-items-center text-muted small mt-1 flex-wrap">
                  {year?.name && (
                    <span className="me-2">
                      <i className="bx bx-calendar me-1"></i>
                      {year.name}
                    </span>
                  )}
                  {engine_c_c?.name && (
                    <span className="me-2">
                      <i className="bx bx-chip me-1"></i>
                      {engine_c_c.name}cc
                    </span>
                  )}
                  {chassis_code?.name && (
                    <span className="me-2">
                      <i className="bx bx-barcode me-1"></i>
                      {chassis_code.name}
                    </span>
                  )}
                  {full_chassis && (
                    <span className="me-2">
                      <i className="bx bx-barcode me-1"></i>
                      {full_chassis}
                    </span>
                  )}
                  {full_engine && (
                    <span className="me-2">
                      <i className="bx bx-cog me-1"></i>
                      {full_engine}
                    </span>
                  )}
                  {tire_size && (
                    <span className="me-2">
                      <i className="bx bx-tire me-1"></i>
                      {tire_size}
                    </span>
                  )}
                </div>
              </div>
              {auction_grade?.name && (
                <Badge color="dark" className="fw-normal">
                  <i className="bx bx-star me-1"></i>
                  {auction_grade.name}
                </Badge>
              )}
            </div>
            {short_descriptions && (
              <p className="text-secondary mb-2 small">{short_descriptions}</p>
            )}
          </div>

          {/* Key Specifications Grid */}
          <Row className="g-2 mb-2 small">
            <Col xs="6" className="d-flex align-items-center">
              <i className="bx bx-tachometer text-primary me-2"></i>
              <span className="text-muted me-1">Mileage:</span>
              <span className="fw-semibold">{formatNumber(mileage)} km</span>
            </Col>
            <Col xs="6" className="d-flex align-items-center">
              <i className="bx bx-cog text-primary me-2"></i>
              <span className="text-muted me-1">Trans:</span>
              <span className="fw-semibold">{transmission?.name}</span>
            </Col>
            <Col xs="6" className="d-flex align-items-center">
              <i className="bx bx-palette text-primary me-2"></i>
              <span className="text-muted me-1">Color:</span>
              <span className="fw-semibold">{exterior_color?.name}</span>
            </Col>
            <Col xs="6" className="d-flex align-items-center">
              <i className="bx bx-gas-pump text-primary me-2"></i>
              <span className="text-muted me-1">Fuel:</span>
              <span className="fw-semibold">{fuel_type?.name}</span>
            </Col>
            <Col xs="6" className="d-flex align-items-center">
              <i className="bx bx-wrench text-primary me-2"></i>
              <span className="text-muted me-1">Engine:</span>
              <span className="fw-semibold">{engine_type?.name}</span>
            </Col>
            <Col xs="6" className="d-flex align-items-center">
              <i className="bx bx-key text-primary me-2"></i>
              <span className="text-muted me-1">Key:</span>
              <span className="fw-semibold">{key_type?.name}</span>
            </Col>
            {interior_color?.name && (
              <Col xs="6" className="d-flex align-items-center">
                <i className="bx bx-paint-roll text-primary me-2"></i>
                <span className="text-muted me-1">Interior:</span>
                <span className="fw-semibold">{interior_color.name}</span>
              </Col>
            )}
            {brta_reg_no && (
              <Col xs="6" className="d-flex align-items-center">
                <i className="bx bx-id-card text-primary me-2"></i>
                <span className="text-muted me-1">Reg No:</span>
                <span className="fw-semibold">{brta_reg_no}</span>
              </Col>
            )}
          </Row>

          {/* Features */}
          {features && features.length > 0 && (
            <div className="mb-2">
              <div className="d-flex align-items-center mb-1">
                <i className="bx bx-list-check text-primary me-1 small"></i>
                <small className="text-muted fw-semibold">Features:</small>
              </div>
              <div className="d-flex flex-wrap gap-1">
                {features.slice(0, 4).map(feature => (
                  <Badge
                    key={feature.id}
                    color="outline-primary"
                    className="fw-normal badge-sm"
                  >
                    {feature.name}
                  </Badge>
                ))}
                {features.length > 4 && (
                  <Badge
                    color="outline-secondary"
                    className="fw-normal badge-sm"
                  >
                    +{features.length - 4} more
                  </Badge>
                )}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="mt-auto d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center flex-wrap small text-muted">
              {seats && (
                <span className="me-2">
                  <i className="bx bx-chair me-1"></i>
                  {seats} Seats
                </span>
              )}
              {doors && (
                <span className="me-2">
                  <i className="bx bx-door-open me-1"></i>
                  {doors} Doors
                </span>
              )}
              {showroom?.short_name && (
                <span className="me-2">
                  <i className="bx bx-store me-1"></i>
                  {showroom.short_name}
                </span>
              )}
              {remarks && (
                <span>
                  <i className="bx bx-message-rounded-dots me-1"></i>
                  {remarks}
                </span>
              )}
            </div>
            <Button color="primary" size="sm">Add To Quotation</Button>
          </div>
        </CardBody>
      </div>
    </Card>
  )
}

export default VehicleShortInfoCard
